import { Box, TextField, Typography } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { SupplierMembersDataGrid } from "../components/SupplierMembersDataGrid";
import { useSupplierMembersList } from "../hooks/useSupplierMembersList";

import type { SupplierMember } from "../types/UserTypes";
import type { ImpersonationState } from "@repo/server-api/impersonation";

export const SupplierImpersonationPage = () => {
	const { t } = useTranslation();
	const { keycloak } = useKeycloak();

	const {
		members,
		rowCount,
		isLoading,
		searchValue,
		setSearchValue,
		paginationModel,
		setPaginationModel,
	} = useSupplierMembersList();

	const handleImpersonate = useCallback(
		(targetMember: SupplierMember) => {
			if (!keycloak.authenticated) {
				console.error("Not authenticated");
				return;
			}

			const state: ImpersonationState = {
				targetUserId: null,
				targetContactId: targetMember.id,
				adminUsername: keycloak.tokenParsed?.preferred_username || "admin",
				startUrl: window.location.href,
			};

			const newWindow = window.open(window.location.origin, "_blank");

			if (newWindow) {
				const checkInterval = setInterval(() => {
					try {
						if (newWindow.document.readyState === "complete") {
							clearInterval(checkInterval);
							newWindow.postMessage(
								{ type: "START_IMPERSONATION", state },
								window.location.origin,
							);
						}
					} catch {
						// Ignore cross-origin errors during loading
					}
				}, 100);

				setTimeout(() => clearInterval(checkInterval), 5000);
			} else {
				alert(`${t("impersonation-failed")}: ${t("popup-blocked")}`);
			}
		},
		[keycloak, t],
	);

	return (
		<Box sx={{ p: 2 }}>
			<Typography variant="h5" sx={{ mb: 2, color: "primary.main" }}>
				{t("members")}
			</Typography>

			<TextField
				size="small"
				placeholder={t("search")}
				value={searchValue}
				onChange={(e) => setSearchValue(e.target.value)}
				sx={{ mb: 2, width: "100%" }}
				data-testid="supplier-members-search-input"
			/>

			<SupplierMembersDataGrid
				rows={members}
				rowCount={rowCount}
				isLoading={isLoading}
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				onImpersonateUser={handleImpersonate}
			/>
		</Box>
	);
};
